import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import EditCategoryForm from '../../components/admin/category/EditCategoryForm';
import PhaseTable from '../../components/admin/category/PhaseTable';
import PhaseModal from '../../components/admin/category/PhaseModal';
import {
  buscarCategoriaTorneioPorId,
  atualizarCategoriaTorneio,
  criarCategoriaTorneio,
  deletarTodasEntidadesCategoria,
  CategoriaTorneio,
  CategoriaTorneioInput,
  FaseCategoriaTorneio
} from '../../services/categoriaTorneioService';
import { buscarValidarElegibilidadeTorneio, buscarTorneioPorId } from '../../services/torneioService';

import {
  criarFase,
  atualizarFase,
  deletarFase,
  reordenarFases,
} from '../../services/faseCategoriaTorneioService';
import { EditCategoryFormValues, PhaseFormValues, toCategoriaTorneioInput } from '../../schemas/category/categorySchemas';
import Header from '../../components/Header';
import { PageBreadcrumb, Crumb } from '../../components/navigation';
import { StatusCategoriaTorneio, TipoPontuacao } from '../../types/categoriaTorneio';
import { useCategoriaLiga } from '../../hooks/useCategoriaLiga';
import type { CategoriaLiga } from '../../types/categoriaLiga';
type EditCategoryInitialValues = EditCategoryFormValues & { quantidadeFases: number; torneioNome: string };

interface EditCategoryLocationState {
  torneioId?: string;
  torneioNome?: string;
}

const EditCategoryTournament: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { torneioId: fallbackTorneioId, torneioNome: fallbackTorneioNome } =
    (location.state as EditCategoryLocationState | undefined) || {};
  const isCreate = !id;

  const [category, setCategory] = useState<CategoriaTorneio | null>(null);
  const [loading, setLoading] = useState<boolean>(!isCreate);
  const [error, setError] = useState<string | null>(null);
  const [phases, setPhases] = useState<FaseCategoriaTorneio[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [editingPhase, setEditingPhase] = useState<FaseCategoriaTorneio | null>(null);
  const [savingPhase, setSavingPhase] = useState<boolean>(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [torneioElegibilidadeFlag, setTorneioElegibilidadeFlag] = useState<boolean | null>(null);
  const { listar: listarCategoriasLiga } = useCategoriaLiga();
  const [categoriasLigaTemplate, setCategoriasLigaTemplate] = useState<CategoriaLiga[]>([]);

  // Catálogo de CategoriaLiga da liga do torneio → templates para o seletor de
  // pré-preenchimento (AC-2). Resolve o ligaId de `category.torneio.liga.id` (modo
  // edição) ou, no modo CRIAR (sem categoria carregada ainda), buscando o torneio
  // por id — o fallback previsto na Task 9. Sem liga (torneio avulso) o seletor não
  // aparece (AC-5, fluxo sem template intacto).
  useEffect(() => {
    let active = true;
    (async () => {
      let ligaIdDoTorneio = category?.torneio?.liga?.id ?? null;
      const torneioIdParaLiga = category?.torneio?.id || fallbackTorneioId || '';
      if (!ligaIdDoTorneio && torneioIdParaLiga) {
        try {
          const torneio = await buscarTorneioPorId(torneioIdParaLiga);
          ligaIdDoTorneio = torneio?.liga?.id ?? null;
        } catch {
          ligaIdDoTorneio = null;
        }
      }
      if (!ligaIdDoTorneio) {
        if (active) setCategoriasLigaTemplate([]);
        return;
      }
      try {
        const list = await listarCategoriasLiga(ligaIdDoTorneio);
        if (active) setCategoriasLigaTemplate(list ?? []);
      } catch {
        if (active) setCategoriasLigaTemplate([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [category?.torneio?.liga?.id, category?.torneio?.id, fallbackTorneioId, listarCategoriasLiga]);

  const load = useCallback(async () => {
    if (!id) {
      setCategory(null);
      setPhases([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await buscarCategoriaTorneioPorId(id);
      setCategory(data);
      setPhases(data.fasesCategoria);
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar categoria');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const resolvedTorneioId = category?.torneio?.id || fallbackTorneioId || '';
  const resolvedTorneioNome = category?.torneio?.nome || fallbackTorneioNome || '';

  useEffect(() => {
    let active = true;
    if (!resolvedTorneioId) {
      setTorneioElegibilidadeFlag(null);
      return;
    }
    buscarValidarElegibilidadeTorneio(resolvedTorneioId).then((v) => {
      if (active) setTorneioElegibilidadeFlag(v);
    });
    return () => {
      active = false;
    };
  }, [resolvedTorneioId]);

  const resolvedValidarElegibilidade =
    torneioElegibilidadeFlag ??
    category?.torneio?.validarElegibilidade ??
    false;

  const breadcrumbItems: Crumb[] = useMemo(() => {
    const base: Crumb[] = [
      { label: 'Início', href: '/' },
    ];

    if (resolvedTorneioId) {
      base.push({
        label: resolvedTorneioNome || 'Torneio',
        href: `/liga/tournament/${resolvedTorneioId}`,
      });
    }

    base.push({
      label: isCreate ? 'Nova Categoria' : (category?.nome || 'Categoria'),
    });

    return base;
  }, [resolvedTorneioId, resolvedTorneioNome, isCreate, category?.nome]);

  const handleBack = () => {
    if (resolvedTorneioId) {
      const tournamentPath = '/admin/edittournament/' + resolvedTorneioId;
      navigate(tournamentPath, {
        replace: true,
        state: {
          torneioId: resolvedTorneioId,
          torneioNome: resolvedTorneioNome,
          refreshCategories: true,
        },
      });
    } else {
      navigate(-1);
    }
  };

  const handleCategorySave = async (values: EditCategoryFormValues) => {
    if (!resolvedTorneioId) {
      alert('Torneio nao informado');
      return;
    }

    const categoriaTorneioInput = toCategoriaTorneioInput(values, resolvedTorneioId);

    try {
      if (isCreate) {
        const created = await criarCategoriaTorneio(categoriaTorneioInput);
        setCategory(created);
        setPhases(created.fasesCategoria ?? []);
        alert('Categoria criada com sucesso');
        navigate(`/admin/editcategorytournament/${created.id}`, {
          replace: true,
          state: {
            torneioId: created.torneio?.id,
            torneioNome: created.torneio?.nome,
          },
        });
      } else {
        const updated = await atualizarCategoriaTorneio(id!, categoriaTorneioInput);
        setCategory((prev) => (prev ? { ...prev, ...updated } : updated));
        alert('Categoria atualizada com sucesso');
      }
    } catch (e: any) {
      alert(e.message || (isCreate ? 'Erro ao criar categoria' : 'Erro ao atualizar categoria'));
    }
  };

  const handlePhaseSave = async (values: PhaseFormValues) => {
    if (!id) return;
    setSavingPhase(true);
    const tipoGrupoChave = values.tipoGrupoChave ?? 'GRUPO';
    const phaseInput: Omit<PhaseFormValues, 'id'> = {
      categoriaTorneioId: id,
      dataFim: values.dataFim,
      dataInicio: values.dataInicio,
      status: values.status,
      ordem: values.ordem,
      nome: values.nome,
      tipoGrupoChave,
    };
    try {
      if (editingPhase) {
        const updated = await atualizarFase(editingPhase.id, phaseInput);
        setPhases((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
      } else {
        const created = await criarFase({
          ...phaseInput,
          ordem: phases.length + 1,
        });
        setPhases((prev) => [...prev, created]);
      }
      setModalOpen(false);
      setEditingPhase(null);
    } catch (e: any) {
      alert(e.message || 'Erro ao salvar fase');
    } finally {
      setSavingPhase(false);
    }
  };

  const handlePhaseDelete = async (fase: FaseCategoriaTorneio) => {
    if (!window.confirm('Tem certeza que deseja excluir esta fase? Esta acao nao pode ser desfeita.')) return;
    try {
      await deletarFase(fase.id);
      setPhases((prev) => prev.filter((f) => f.id !== fase.id));
    } catch (e: any) {
      alert(e.message || 'Erro ao excluir fase');
    }
  };

  const handleReorder = async (items: FaseCategoriaTorneio[]) => {
    setPhases(items);
    try {
      await reordenarFases(items.map((f) => ({ id: f.id, ordem: f.ordem })));
    } catch (e: any) {
      alert(e.message || 'Erro ao reordenar fases');
    }
  };

  const hasMatches = category?.partidas && category.partidas.length > 0;

  const handleResetMatches = async () => {
    if (!id) return;
    
    try {
      await deletarTodasEntidadesCategoria(id);
      setResetDialogOpen(false);
      alert('Partidas resetadas com sucesso');
      await load();
    } catch (e: any) {
      alert(e.message || 'Erro ao resetar partidas');
    }
  };

  if (loading) return <div>Carregando...</div>;
  if (error)
    return (
      <div className="alert alert-danger">
        {error}
        <button className="btn btn-link" onClick={load}>
          Tentar novamente
        </button>
      </div>
    );
  if (!category && !isCreate) return null;

  const initialValues: EditCategoryInitialValues = isCreate
    ? {
        nome: '',
        descricao: '',
        genero: 'MISTO',
        numeroAtletas: 1,
        precoInscricao: 0,
        regulamento: '',
        quantidadeFases: 0,
        torneioNome: resolvedTorneioNome,
        status: StatusCategoriaTorneio.AGENDADA,
        aplicarDescontoTorneio: false,
        quantidadeGrupos: undefined,
        classificadosPorGrupo: undefined,
        tipoPontuacao: undefined,
        numeroSets: undefined,
        gamesPorSets: undefined,
        tiebreaks: undefined,
      }
    : {
        nome: category?.nome || '',
        descricao: category?.descricao || '',
        genero: category?.genero || 'MISTO',
        numeroAtletas: category?.numeroAtletas ?? 1,
        precoInscricao: category?.precoInscricao ?? 0,
        regulamento: category?.regulamento || '',
        quantidadeFases: phases.length,
        torneioNome: resolvedTorneioNome,
        status: (category?.status as EditCategoryInitialValues['status']) || StatusCategoriaTorneio.AGENDADA,
        aplicarDescontoTorneio: category?.aplicarDescontoTorneio ?? false,
        // null = "herda do torneio"; preservar explicitamente (não usar ?? false).
        configFilaEspera: category?.configFilaEspera ?? null,
        vagas: category?.vagas ?? undefined,
        dataInicio: category?.dataInicio || undefined,
        dataFim: category?.dataFim || undefined,
        dataLimPag: category?.dataLimPag || undefined,
        modeloDisputa: (category?.modeloDisputa as CategoriaTorneioInput['modeloDisputa']) || undefined,
        idadeInicial: category?.idadeInicial ?? undefined,
        idadeFinal: category?.idadeFinal ?? undefined,
        idadeCombinada: category?.idadeCombinada ?? undefined,
        quantidadeGrupos: category?.quantidadeGrupos ?? undefined,
        classificadosPorGrupo: category?.classificadosPorGrupo ?? undefined,
        tamanhoGrupo: category?.tamanhoGrupo ?? undefined,
        tipoPontuacao: (category?.tipoPontuacao && Object.values(TipoPontuacao).includes(category.tipoPontuacao as TipoPontuacao))
          ? (category.tipoPontuacao as TipoPontuacao)
          : undefined,
        numeroSets: category?.numeroSets ?? undefined,
        gamesPorSets: category?.gamesPorSets ?? undefined,
        tiebreaks: category?.tiebreaks ?? undefined,
        categoriaLigaId: category?.categoriaLiga?.id ?? undefined,
      };

  return (
    <div className="main-wrapper">
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="middle-wrap">
              <div className="container py-3">
                <div className="mb-3">
                  <PageBreadcrumb
                    id="category-breadcrumb"
                    items={breadcrumbItems}
                    onBack={handleBack}
                    variant="bootstrap"
                  />
                </div>

                <h2 className="mb-0">{isCreate ? 'Criar Categoria de Torneio' : 'Editar Categoria de Torneio'}</h2>
                {resolvedTorneioNome ? <p className="text-muted">{resolvedTorneioNome}</p> : null}

                {hasMatches && !isCreate && (
                  <div className="mb-3">
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => setResetDialogOpen(true)}
                    >
                      Resetar partidas
                    </button>
                  </div>
                )}

                <EditCategoryForm
                  initialValues={initialValues}
                  onSubmit={handleCategorySave}
                  onCancel={() => (isCreate ? handleBack() : load())}
                  hasMatches={hasMatches}
                  modeloDisputa={category?.modeloDisputa}
                  isCreate={isCreate}
                  validarElegibilidade={resolvedValidarElegibilidade}
                  categoriasLigaTemplate={categoriasLigaTemplate}
                />

                {isCreate ? (
                  <div className="alert alert-info mt-3">
                    Salve a categoria para habilitar a gestao de fases.
                  </div>
                ) : (
                  <>
                    <PhaseTable
                      phases={phases}
                      onCreate={() => {
                        setEditingPhase(null);
                        setModalOpen(true);
                      }}
                      onEdit={(fase) => {
                        setEditingPhase(fase);
                        setModalOpen(true);
                      }}
                      onDelete={handlePhaseDelete}
                      onReorder={handleReorder}
                    />

                    <PhaseModal
                      open={modalOpen}
                      initialValues={
                        editingPhase
                          ? {
                              ...editingPhase,
                              status:
                                editingPhase.status === 'AGENDADA' ||
                                editingPhase.status === 'EM_ANDAMENTO' ||
                                editingPhase.status === 'FINALIZADA' ||
                                editingPhase.status === 'CANCELADA'
                                  ? editingPhase.status
                                  : undefined,
                              tipoGrupoChave: editingPhase.tipoGrupoChave ?? undefined,
                            }
                          : undefined
                      }
                      onSaved={handlePhaseSave}
                      onClose={() => setModalOpen(false)}
                    />
                  </>
                )}
              </div>

            </div>

          </div>
        </div>
      </div>

      {resetDialogOpen && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setResetDialogOpen(false)} />
          <div
            className="modal fade show d-block"
            role="dialog"
            tabIndex={-1}
            style={{ overflow: 'auto' }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirmar Reset de Partidas</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Fechar"
                    onClick={() => setResetDialogOpen(false)}
                  />
                </div>
                <div className="modal-body">
                  <p>
                    Tem certeza que deseja resetar todas as partidas desta categoria? Esta ação irá deletar todas as partidas e fases atuais. Esta ação não pode ser desfeita.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setResetDialogOpen(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleResetMatches}
                  >
                    Confirmar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EditCategoryTournament;


